import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
} from '@mui/material';
import { ChevronDown, Play, Lock, Forward, Check } from 'lucide-mui';
import { useDispatch, useSelector } from 'react-redux';
import { Chapter } from './types';
import useLimits from '../../../plan/hooks/limits';
import { shareThunk } from '../../selectedVideoSlice';
import { RootState } from '../../../../reducers';
import { getThumbnailByTimestamp } from '../../../../services/fileSystemService';
import Thumbnail from './Thumbnail';

/**
 * Replaces speaker names in text with their correct mapped names.
 * Handles two sources of speaker mappings:
 * 1. textData.speakers - Contains the final correct names (e.g., "SPEAKER_00" -> "Julian")
 * 2. SUMMARY_AND_PROMPTS - Contains additional name variations (e.g., "Friend" -> "SPEAKER_00")
 *
 * Example:
 * textData.speakers = { "SPEAKER_00": { name: "Julian" } }
 * SUMMARY_AND_PROMPTS = { speakers: [{ "SPEAKER_00": "Friend" }] }
 *
 * Result: Both "SPEAKER_00" and "Friend" will be replaced with "Julian"
 */
const replaceSpeakerNames = (text: string, state: RootState) => {
  if (!text) return text;

  let replacedText = text;
  const speakers = state.selectedVideo.file?.textData?.speakers;
  const summaryAndPrompts =
    state.selectedVideo.postProcess?.systemPromptResponses?.SUMMARY_AND_PROMPTS;

  // Store all name variations and their final correct names
  const nameMap = new Map<string, string>();

  // First get the correct names from textData.speakers
  // This maps speaker IDs to their final names (e.g., "SPEAKER_00" -> "Julian")
  if (speakers) {
    Object.entries(speakers).forEach(([speakerId, { name }]) => {
      nameMap.set(speakerId, name);
    });
  }

  // Then find additional name variations from SUMMARY_AND_PROMPTS
  // This helps us map descriptive names to their correct final names
  if (summaryAndPrompts) {
    try {
      const parsed = JSON.parse(summaryAndPrompts.responseText);
      if (parsed.speakers && Array.isArray(parsed.speakers)) {
        parsed.speakers.forEach((speaker: Record<string, string>) => {
          Object.entries(speaker).forEach(([key, value]) => {
            // If we have a final name for this speaker ID, map the descriptive name to it
            // e.g., if "SPEAKER_00" -> "Julian", and we find "SPEAKER_00": "Friend",
            // then map "Friend" -> "Julian"
            const finalName = nameMap.get(key);
            if (finalName) {
              nameMap.set(value, finalName);
            }
          });
        });
      }
    } catch (e) {
      console.error('Error parsing SUMMARY_AND_PROMPTS:', e);
    }
  }

  // Apply all name replacements using word boundaries to only replace whole words
  nameMap.forEach((finalName, originalName) => {
    const regex = new RegExp(`\\b${originalName}\\b`, 'g');
    replacedText = replacedText.replace(regex, finalName);
  });

  return replacedText;
};

/**
 * Converts timestamps in HH:MM:SS format to seconds for comparison
 */
const compareTimestamps = (a: string, b: string): number => {
  const timeToSeconds = (timestamp: string): number => {
    const [hours, minutes, seconds] = timestamp.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  return timeToSeconds(a) - timeToSeconds(b);
};

interface ChapterAccordionProps {
  chapter: Chapter;
  index: number;
  expanded: boolean;
  searchTerm: string;
  showVisualElements: boolean;
  onChange: (
    panel: number
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onTimeClick: (timestamp: string) => void;
  formatTimestamp: (timestamp: string) => string;
  highlightText: (text: string, searchTerm: string) => React.ReactNode;
  currentTime: number;
  isLocked?: boolean;
  allowSharing?: boolean;
}

/**
 * ChapterAccordion component displays a single chapter with:
 * - Expandable accordion UI
 * - Title with speaker names replaced
 * - Notes with speaker names replaced
 * - Timestamp chip for navigation
 * - Share button for creating chapter-specific links
 * - Locked state for premium features
 */
const ChapterAccordion: React.FC<ChapterAccordionProps> = ({
  chapter,
  index,
  expanded,
  searchTerm,
  showVisualElements,
  onChange,
  onTimeClick,
  formatTimestamp,
  highlightText,
  currentTime,
  isLocked = false,
  allowSharing = true,
}) => {
  const theme = useTheme();
  const guardLimits = useLimits();
  const dispatch = useDispatch();
  const reduxState = useSelector((state: RootState) => state);
  const isAudioOnly =
    reduxState.selectedVideo.file?.streams?.length === 1 &&
    reduxState.selectedVideo.file?.streams[0] === 'audio';
  const [isClipCopied, setIsClipCopied] = React.useState(false);
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(null);
  const [isLoadingThumbnail, setIsLoadingThumbnail] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Process chapter title and notes to replace speaker names
  // Memoized to prevent unnecessary processing on every render
  const processedTitle = React.useMemo(
    () => replaceSpeakerNames(chapter.title, reduxState),
    [chapter.title, reduxState]
  );
  const processedNotes = React.useMemo(
    () => replaceSpeakerNames(chapter.notes, reduxState),
    [chapter.notes, reduxState]
  );

  // Only fetch thumbnail if visual elements are enabled
  React.useEffect(() => {
    const fetchThumbnail = async () => {
      if (
        showVisualElements &&
        !thumbnailUrl &&
        reduxState.selectedVideo.file?._id
      ) {
        setIsLoadingThumbnail(true);
        const timeToSeconds = (timestamp: string): number => {
          const [hours, minutes, seconds] = timestamp.split(':').map(Number);
          return hours * 3600 + minutes * 60 + seconds;
        };

        try {
          const url = await getThumbnailByTimestamp({
            fileId: reduxState.selectedVideo.file._id,
            timestamp: timeToSeconds(chapter.start),
          });
          setThumbnailUrl(url);
        } catch (error) {
          console.error('Error fetching thumbnail:', error);
        } finally {
          setIsLoadingThumbnail(false);
        }
      }
    };

    fetchThumbnail();
  }, [chapter.start, reduxState.selectedVideo.file?._id, showVisualElements]);

  /**
   * Handles expanding/collapsing the accordion
   * For locked chapters, prevents expansion and shows upgrade prompt
   */
  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isLocked) {
      event.preventDefault();
      event.stopPropagation();
      guardLimits('fullChapterAccess', () => {
        return true;
      });
    } else {
      onChange(index)(event, isExpanded);
    }
  };

  // Monitor current playback time to detect if this chapter is currently playing
  React.useEffect(() => {
    const timeToSeconds = (timestamp: string): number => {
      const [hours, minutes, seconds] = timestamp.split(':').map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    };

    const startTime = timeToSeconds(chapter.start);
    const endTime = chapter.end ? timeToSeconds(chapter.end) : Infinity;

    console.log({
      currentTime,
      startTime,
      endTime,
      isPlaying:
        currentTime > 0 && currentTime >= startTime && currentTime < endTime,
    });
  }, [currentTime, chapter.start, chapter.end]);

  /**
   * Determines if the current playback time is within this chapter's timespan
   * Used to highlight the currently playing chapter
   */
  const isPlaying = React.useMemo(() => {
    const timeToSeconds = (timestamp: string): number => {
      const [hours, minutes, seconds] = timestamp.split(':').map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    };

    const startTime = timeToSeconds(chapter.start);
    const endTime = chapter.end ? timeToSeconds(chapter.end) : Infinity;

    return (
      Math.abs(currentTime) > 0.1 &&
      currentTime >= startTime &&
      currentTime < endTime
    );
  }, [chapter.start, chapter.end, currentTime]);

  /**
   * Handles sharing a clip of this chapter:
   * 1. Gets or creates a share ID for the video
   * 2. Constructs a URL with start/end timestamps
   * 3. Copies URL to clipboard
   * 4. Shows success indicator
   * 5. Opens shared clip in new window
   */
  const handleClipShare = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent any default navigation
    e.stopPropagation(); // Stop event from bubbling up

    // Convert HH:MM:SS to seconds for start and end
    const timeToSeconds = (timestamp: string): number => {
      const [hours, minutes, seconds] = timestamp.split(':').map(Number);
      return hours * 3600 + minutes * 60 + seconds;
    };

    const startTime = timeToSeconds(chapter.start);
    const endTime = chapter.end ? timeToSeconds(chapter.end) : startTime + 60; // Default to 1 minute if no end time

    // Get existing shareId or create new one
    const shareId = reduxState.selectedVideo.file.share?.shareId;
    if (shareId) {
      const basePath = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash if present
      const shareUrl = `${window.location.origin}${basePath}/#/shared/${shareId}?start=${startTime}&end=${endTime}`;
      await navigator.clipboard.writeText(shareUrl);
      setIsClipCopied(true);
      setTimeout(() => setIsClipCopied(false), 2000);
      // Open in new window after a short delay
      setTimeout(() => {
        window.open(shareUrl, '_blank', 'noopener');
      }, 500);
    } else {
      // Only dispatch if we don't have a shareId
      await dispatch(
        shareThunk({
          fileId: reduxState.selectedVideo.file._id,
          copyUrlToClipboard: false,
        })
      );
      const newShareId = reduxState.selectedVideo.file.share?.shareId;
      if (newShareId) {
        const basePath = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash if present
        const shareUrl = `${window.location.origin}${basePath}/#/shared/${newShareId}?start=${startTime}&end=${endTime}`;
        await navigator.clipboard.writeText(shareUrl);
        setIsClipCopied(true);
        setTimeout(() => setIsClipCopied(false), 2000);
        // Open in new window after a short delay
        setTimeout(() => {
          window.open(shareUrl, '_blank', 'noopener');
        }, 500);
      }
    }
  };

  return (
    <>
      <Accordion
        expanded={expanded && !isLocked}
        onChange={handleChange}
        disableGutters
        sx={{
          '&:before': {
            display: 'none',
          },
          boxShadow: 'none',
          borderRadius: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'transparent',
          '&:first-of-type': {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          transition: (t) =>
            t.transitions.create(['margin', 'background-color'], {
              duration: t.transitions.duration.shortest,
            }),
          '& .MuiAccordionSummary-root': {
            backgroundColor: isPlaying
              ? 'secondary.light'
              : 'background.default',
            opacity: isLocked ? 0.7 : 1,
            '& .MuiTypography-root': {
              color: isLocked ? 'text.disabled' : 'text.primary',
            },
            '& .timestamp-chip': {
              opacity: showVisualElements ? 1 : 0,
              transition: 'opacity 0.2s',
            },
            '&:hover .timestamp-chip': {
              opacity: 1,
            },
          },
          '&.Mui-expanded .MuiAccordionSummary-root .timestamp-chip': {
            opacity: 1,
          },
          '&:hover .MuiAccordionSummary-root': {
            backgroundColor: isPlaying
              ? theme.palette.secondary.light
              : 'action.hover',
          },
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
            cursor: 'pointer',
            opacity: 1,
          },
          '& .MuiAccordionSummary-root.Mui-disabled': {
            opacity: 0.7,
            cursor: 'pointer',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ChevronDown />}
          sx={{
            minHeight: 'unset',
            padding: (t) => t.spacing(0.75, 1.5),
            '& .MuiAccordionSummary-content': {
              margin: '4px 0',
              '&.Mui-expanded': {
                margin: '4px 0',
              },
            },
            '&:hover .share-button': {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
            }}
          >
            {isLocked ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Lock sx={{ color: 'text.disabled', fontSize: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'text.disabled',
                      fontWeight: 600,
                      flex: 1,
                    }}
                  >
                    Upgrade to unlock
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {highlightText(processedTitle, searchTerm)}
                  </Typography>
                  {/* Container for share button and timestamp */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      ml: 'auto',
                    }}
                  >
                    {/* Share button */}
                    {allowSharing && (
                      <Tooltip title="Share clip" placement="bottom" arrow>
                        <IconButton
                          size="small"
                          onClick={handleClipShare}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          className="share-button"
                          sx={{
                            opacity: 0,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            color: isClipCopied
                              ? 'success.main'
                              : 'primary.main',
                            padding: '4px',
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }}
                        >
                          {isClipCopied ? (
                            <Check fontSize="small" />
                          ) : (
                            <Forward fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Timestamp chip */}
                    <Chip
                      className="timestamp-chip"
                      icon={
                        <Play
                          sx={{
                            fontSize: '18px',
                            opacity: 0,
                            transform: 'scale(0.8)',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            mr: '-8px',
                            color: 'primary.main',
                            width: 0,
                            overflow: 'hidden',
                          }}
                        />
                      }
                      label={formatTimestamp(chapter.start).replace(/^00:/, '')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onTimeClick(chapter.start);
                      }}
                      clickable
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'background.paper',
                        color: 'primary.main',
                        height: '24px',
                        opacity: showVisualElements ? 1 : 0,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        width: 'auto',
                        border: '1px solid transparent',
                        '&:hover': {
                          backgroundColor: 'background.paper',
                          opacity: '1 !important',
                          border: '1px solid',
                          borderColor: 'primary.main',
                          '& .MuiSvgIcon-root': {
                            opacity: 1,
                            transform: 'scale(1)',
                            width: '18px',
                            color: 'primary.main',
                          },
                        },
                        '& .MuiChip-label': {
                          padding: '0 8px',
                          paddingLeft: '4px !important',
                          color: 'primary.main',
                          fontSize: '0.875rem',
                          textAlign: 'center',
                          lineHeight: 1,
                        },
                        '& .MuiChip-icon': {
                          ml: 0,
                          mr: 0,
                          pl: 0.5,
                          transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          width: 0,
                        },
                      }}
                    />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </AccordionSummary>
        {/* Chapter notes section - only shown if chapter is not locked */}
        {!isLocked && (
          <AccordionDetails
            sx={{
              pt: 0.75,
              pb: 1.5,
              px: 1.5,
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
            {/* Thumbnail Section - only show if visual elements are enabled and not audio only */}
            {showVisualElements && !isAudioOnly && (
              <Thumbnail
                url={thumbnailUrl}
                isLoading={isLoadingThumbnail}
                title={processedTitle}
                onTimeClick={() => onTimeClick(chapter.start)}
                onExpand={() => setIsFullscreen(true)}
              />
            )}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                flex: 1,
                minWidth: 0,
              }}
            >
              {highlightText(processedNotes, searchTerm)}
            </Typography>
          </AccordionDetails>
        )}
      </Accordion>

      {/* Fullscreen Dialog - only for video files */}
      {!isAudioOnly && (
        <Dialog
          open={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              backgroundColor: 'background.paper',
              borderRadius: 2,
              overflow: 'hidden',
              margin: 2,
              width: {
                xs: '90vw',
                sm: '60vw',
              },
              maxHeight: '90vh',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
            }}
          >
            {/* Image Container */}
            <Box
              sx={{
                width: '100%',
                position: 'relative',
                paddingTop: '56.25%', // 16:9 aspect ratio
                backgroundColor: 'background.default',
              }}
            >
              {thumbnailUrl && (
                <img
                  src={thumbnailUrl}
                  alt={`Thumbnail for ${processedTitle}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              )}
            </Box>

            {/* Text Content */}
            <Box
              sx={{
                p: 2,
                flexShrink: 0,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                {processedTitle}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  whiteSpace: 'pre-wrap',
                  maxHeight: {
                    xs: '20vh',
                    sm: '25vh',
                  },
                  overflowY: 'auto',
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {processedNotes}
              </Typography>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export { compareTimestamps, replaceSpeakerNames };
export default ChapterAccordion;
